import { useEffect, useCallback, useMemo } from "react";
import { UseFormReturn, FieldValues } from "react-hook-form";
import { useDraft } from "../settings/use-draft";
import { useFormFeedback } from "./use-feedback";

interface UseFormManagerOptions<T extends FieldValues> {
  form: UseFormReturn<T>;
  draftKey: string;
  isOpen?: boolean;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  successMessage?: string;
  autoSaveDraft?: boolean;
}

export function useFormManager<T extends FieldValues>(options: UseFormManagerOptions<T>) {
  const {
    form,
    draftKey,
    isOpen = true,
    onSuccess,
    onError,
    successMessage,
    autoSaveDraft = true,
  } = options;

  // Initialize draft management
  const [draft, setDraft, clearDraft] = useDraft<T>(draftKey, form.getValues());

  // Initialize feedback management
  const feedback = useFormFeedback({
    successMessage,
    autoResetError: true,
  });

  // Sync form with draft when modal opens
  useEffect(() => {
    if (isOpen && draft) {
      form.reset({ ...form.getValues(), ...draft });
    }
  }, [isOpen, draft, form]);

  // Manual draft saving function
  const saveDraft = useCallback(
    (values: T) => {
      if (autoSaveDraft) {
        setDraft(values);
      }
    },
    [autoSaveDraft, setDraft]
  );

  // Enhanced mutation handlers with draft clearing
  const createMutationHandlers = useCallback(
    (
      customOptions: {
        onSuccess?: () => void;
        onError?: (error: any) => void;
        successMessage?: string;
        clearDraftOnSuccess?: boolean;
      } = {}
    ) => {
      const { onSuccess, onError, successMessage, clearDraftOnSuccess = true } = customOptions;

      return feedback.createMutationHandlers({
        onSuccess: () => {
          if (clearDraftOnSuccess) {
            clearDraft();
          }
          onSuccess?.();
        },
        onError,
        successMessage,
      });
    },
    [feedback, clearDraft]
  );

  // Memoized return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Draft management
      draft,
      setDraft,
      clearDraft,
      saveDraft,

      // Feedback management
      ...feedback,

      // Enhanced mutation handlers
      createMutationHandlers,

      // Form utilities
      resetForm: () => {
        form.reset();
        clearDraft();
        feedback.reset();
      },
    }),
    [draft, setDraft, clearDraft, saveDraft, feedback, createMutationHandlers, form]
  );
}
